import logo from './logo.png';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
// Re-enable with the cart link below when checkout returns.
// import { useCart } from './CartContext';
import {
  GROUPS,
  productsInGroup,
  formatTier,
  thumbSrc,
  type Price,
  type Product
} from './products';
import {
  LOCATIONS,
  PICKUP_HOURS,
  PAYMENT_METHODS,
  BUSINESS_PHONE
} from './pickup';

const PHONE_HREF = 'tel:+12517515962';
const SMS_HREF = 'sms:+12517515962';

/* Outline icons (Heroicons v2), inline rather than from an icon font: the font
   was a render-blocking request to Google on every visit, and while it loaded
   the icons showed as their ligature names ("phone_in_talk"). */
type IconProps = { className?: string; strokeWidth?: number };

const PhoneIcon = ({ className, strokeWidth = 1.8 }: IconProps) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
  </svg>
);

const MailIcon = ({ className, strokeWidth = 1.8 }: IconProps) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
  </svg>
);

const MapPinIcon = ({ className, strokeWidth = 1.8 }: IconProps) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    <path d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
  </svg>
);

/** A quoted-by-phone tier. Rendered as a filled pill so it carries the same
 *  visual weight as a price — it is a value, not a missing one — and so it
 *  dials in one tap from the yard. */
const CallPill = ({ tierLabel }: { tierLabel?: string }) => (
  <a
    href={PHONE_HREF}
    aria-label={tierLabel ? `Call for ${tierLabel} pricing` : 'Call for pricing'}
    className="inline-flex min-h-[30px] items-center gap-1 rounded-full bg-amber-400 px-2 text-xs font-extrabold uppercase tracking-wide text-amber-900 ring-1 ring-inset ring-amber-600 hover:bg-amber-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-2"
  >
    {/* Same icon the footer uses for "call", so the affordance reads the same
        site-wide. Hidden on the narrowest screens, where the column has no room. */}
    <PhoneIcon className="hidden h-3 w-3 shrink-0 sm:block" strokeWidth={2.4} />
    Call
  </a>
);

/** The site's original HOT SALE mark, kept verbatim from the previous card design
 *  and resized to sit as an overlay on the product thumbnail. */
const HotSaleBadge = () => (
  <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-red-500 to-orange-500 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-white shadow-sm">
    <svg className="h-3 w-3 shrink-0" viewBox="0 0 92.27 122.88" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path fill="white" d="M18.61,54.89C15.7,28.8,30.94,10.45,59.52,0C42.02,22.71,74.44,47.31,76.23,70.89 c4.19-7.15,6.57-16.69,7.04-29.45c21.43,33.62,3.66,88.57-43.5,80.67c-4.33-0.72-8.5-2.09-12.3-4.13C10.27,108.8,0,88.79,0,69.68 C0,57.5,5.21,46.63,11.95,37.99C12.85,46.45,14.77,52.76,18.61,54.89L18.61,54.89z"/>
      <path fill="white" fillOpacity="0.7" d="M33.87,92.58c-4.86-12.55-4.19-32.82,9.42-39.93c0.1,23.3,23.05,26.27,18.8,51.14 c3.92-4.44,5.9-11.54,6.25-17.15c6.22,14.24,1.34,25.63-7.53,31.43c-26.97,17.64-50.19-18.12-34.75-37.72 C26.53,84.73,31.89,91.49,33.87,92.58L33.87,92.58z"/>
    </svg>
    Hot Sale
  </span>
);

const PriceCell = ({ value, tierLabel }: { value: Price; tierLabel?: string }) => (
  <td className="whitespace-nowrap px-1 py-1.5 text-right text-base font-extrabold tracking-tight text-gray-900 sm:px-1.5 sm:text-lg">
    {value === 'CALL' ? <CallPill tierLabel={tierLabel} /> : formatTier(value)}
  </td>
);

/**
 * The price table. Column positions are fixed by a shared colgroup so tier-1
 * prices line up down the whole page, which is the point of the list layout.
 *
 * Band labels are repeated on every product rather than pinned once at the top
 * of the page: the flyer uses seven different quantity ladders, so a single
 * shared header would mislabel most of the catalog.
 */
const PriceTable = ({ item }: { item: Product }) => {
  const flat = item.tiers.length === 0;

  return (
    <table className="w-full table-fixed border-collapse tabular-nums">
      <caption className="sr-only">{item.name} pricing</caption>
      <colgroup>
        <col className="w-[32%] sm:w-[30%]" />
        <col className="w-[23%] sm:w-[24%]" />
        <col className="w-[23%] sm:w-[24%]" />
        <col className="w-[22%]" />
      </colgroup>

      {/* Flat-priced products get a band too, so every price table reads the
          same way: pale honey quantity band on top, neutral prices beneath.
          The band is a tint, not solid gold, so the gold Call pills are the
          brightest thing in each row. */}
      <thead>
        <tr className="bg-honey-100">
          {/* Carries the same rule as the band labels; without it the
              strip ran 1px lower over this column. */}
          <td className="border-b border-gray-300" />
          {flat ? (
            <th
              scope="col"
              colSpan={3}
              className="whitespace-nowrap border-b border-gray-300 px-1 py-1 text-right text-xs font-bold tracking-tight text-navy-900 sm:px-1.5"
            >
              Any quantity
            </th>
          ) : (
            item.tiers.map(tier => (
              <th
                key={tier.label}
                scope="col"
                className="whitespace-nowrap border-b border-gray-300 px-1 py-1 text-right text-xs font-bold tracking-tight text-navy-900 sm:px-1.5"
              >
                {/* Display-only squeeze; products.ts keeps the flyer-verbatim string. */}
                {tier.label.replace(/\s*–\s*/g, '–')}
              </th>
            ))
          )}
        </tr>
      </thead>

      <tbody className="divide-y divide-gray-300">
        {flat ? (
          <tr>
            <th
              scope="row"
              className="px-1 py-1 text-left text-xs font-semibold sm:px-1.5 text-gray-600"
            >
              {item.unit}
            </th>
            <td className="whitespace-nowrap px-1 py-1.5 text-right text-base font-extrabold tabular-nums tracking-tight text-gray-900 sm:px-1.5 sm:text-lg" colSpan={3}>
              {formatTier(item.price)}
            </td>
          </tr>
        ) : (
          item.variants.map(variant => (
            <tr key={variant.label}>
              <th
                scope="row"
                className={`px-1 py-1 text-left text-xs leading-tight sm:px-1.5 text-gray-600 ${
                  item.variants.length === 1 ? 'font-normal' : 'font-semibold'
                }`}
              >
                {variant.label}
              </th>
              {variant.prices.map((price, i) => (
                <PriceCell key={i} value={price} tierLabel={item.tiers[i]?.label} />
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
};

/**
 * The existing swipe / tap-zone / dot carousel, unchanged in behaviour. It now
 * lives in the expandable panel beneath a row and stays mounted when collapsed,
 * so reopening a product does not reset the visible image or re-fire the
 * loading spinner.
 */
const Gallery = ({ item }: { item: Product }) => {
  const [currentImage, setCurrentImage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [fadeIn, setFadeIn] = useState(true);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const handleImageChange = (index: number) => {
    setFadeIn(false);
    setTimeout(() => {
      setCurrentImage(index);
      setFadeIn(true);
    }, 100);
  };

  const nextImage = () => {
    if (currentImage < item.images.length - 1) handleImageChange(currentImage + 1);
  };

  const prevImage = () => {
    if (currentImage > 0) handleImageChange(currentImage - 1);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 50) nextImage();
    if (touchStart - touchEnd < -50) prevImage();
  };

  const atStart = currentImage === 0;
  const atEnd = currentImage === item.images.length - 1;

  return (
    <div className="border-t border-gray-300 p-4">
      <div
        onTouchStart={e => {
          // Seed both ends, or a stationary tap reads as a full-width swipe.
          setTouchStart(e.targetTouches[0].clientX);
          setTouchEnd(e.targetTouches[0].clientX);
        }}
        onTouchMove={e => setTouchEnd(e.targetTouches[0].clientX)}
        onTouchEnd={handleTouchEnd}
        className="relative mx-auto flex h-64 w-full max-w-md items-center justify-center overflow-hidden sm:h-72"
      >
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-amber-200 border-t-amber-500" />
          </div>
        )}
        <img
          alt={item.imageAlt}
          src={item.images[currentImage]}
          loading="lazy"
          decoding="async"
          onLoad={() => setIsLoading(false)}
          className={`max-h-full max-w-full object-contain transition-opacity duration-100 ${
            fadeIn ? 'opacity-100' : 'opacity-0'
          }`}
        />

        {item.images.length > 1 && (
          <>
            <button
              type="button"
              onClick={prevImage}
              disabled={atStart}
              aria-label="Previous image"
              className="absolute left-1 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full text-2xl leading-none text-navy-900 drop-shadow-sm hover:text-amber-700 disabled:opacity-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-2"
            >
              <span aria-hidden="true">&#8249;</span>
            </button>
            <button
              type="button"
              onClick={nextImage}
              disabled={atEnd}
              aria-label="Next image"
              className="absolute right-1 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full text-2xl leading-none text-navy-900 drop-shadow-sm hover:text-amber-700 disabled:opacity-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-2"
            >
              <span aria-hidden="true">&#8250;</span>
            </button>
          </>
        )}
      </div>

      {item.images.length > 1 && (
        <div className="mt-1 flex items-center justify-center">
          {item.images.map((_, index) => (
            <button
              key={index}
              type="button"
              aria-label={`Show image ${index + 1} of ${item.images.length}`}
              onClick={() => handleImageChange(index)}
              className="flex h-8 w-5 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-2"
            >
              <span
                className={`block h-1.5 rounded-full transition-all ${
                  index === currentImage ? 'w-4 bg-amber-600' : 'w-1.5 bg-gray-400'
                }`}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ProductRow = ({
  item,
  open,
  onToggle
}: {
  item: Product;
  open: boolean;
  onToggle: (el: HTMLElement) => void;
}) => {
  // Mount the gallery on first open only, so a page load does not fetch a
  // full-size photo for all 14 products. Once mounted it stays mounted, so
  // reopening keeps its place.
  const [hasOpened, setHasOpened] = useState(false);
  const rowRef = useRef<HTMLLIElement>(null);

  const toggle = () => {
    setHasOpened(true);
    if (rowRef.current) onToggle(rowRef.current);
  };

  return (
    <li ref={rowRef} data-row className="group/row mb-2 overflow-hidden rounded-xl bg-white shadow-md transition-shadow [@media(hover:hover)]:hover:shadow-lg">
      <div className="grid grid-cols-[80px_1fr] items-center gap-x-3.5 gap-y-3 p-3 sm:grid-cols-[104px_minmax(150px,1fr)_360px] sm:gap-x-4 lg:grid-cols-[120px_minmax(180px,1fr)_400px]">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-label={`${open ? 'Hide' : 'Show'} photos of ${item.name}`}
          className="group/thumb relative aspect-square overflow-hidden rounded-lg bg-white p-1.5 transition-all active:bg-amber-50 [@media(hover:hover)]:hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-2"
        >
          <img
            alt={item.imageAlt}
            src={thumbSrc(item.images[0])}
            width={240}
            height={240}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-contain transition-transform duration-200 [@media(hover:hover)]:group-hover/thumb:scale-105"
          />
          {/* Hover-only affordance. It sits in the white margin rather than over
              the photo: these are cut-outs, and any wash tints the product itself.
              Touch devices get no hover state, which is fine — the gallery is
              supplementary, every price is already on the row. */}
          <span className="pointer-events-none absolute bottom-1 right-1 hidden text-navy-900 opacity-0 drop-shadow-sm transition-opacity duration-150 [@media(hover:hover)]:block group-hover/thumb:opacity-100">
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M9 4H4v5M4 4l6 6M15 20h5v-5M20 20l-6-6" />
            </svg>
          </span>
        </button>

        <div className="min-w-0">
          {item.isBestSeller && (
            <p className="mb-1">
              <HotSaleBadge />
            </p>
          )}
          <h3 className="text-sm font-bold leading-snug text-navy-950 transition-colors sm:text-base [@media(hover:hover)]:group-hover/row:text-amber-700">
            {item.name}
          </h3>
          {item.spec && <p className="mt-0.5 text-xs text-gray-600">{item.spec}</p>}
          {item.perk && (
            <p className="mt-1.5">
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-900 ring-1 ring-inset ring-amber-600">
                <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z" />
                  <circle cx="7" cy="18" r="1.6" />
                  <circle cx="17.5" cy="18" r="1.6" />
                </svg>
                {item.perk}
              </span>
            </p>
          )}
        </div>

        <div className="col-span-2 sm:col-span-1">
          <PriceTable item={item} />
        </div>
      </div>

      <div
        className={`grid transition-all duration-200 ${
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="overflow-hidden">{hasOpened && <Gallery item={item} />}</div>
      </div>
    </li>
  );
};

/** Worded exactly as the printed flyer lists them. */
const CONTAINER_ITEMS = [
  'Fully assembled deep frames',
  'Fully assembled medium frames',
  'Plastic corrugated nuc boxes',
  'Full container deep and medium unassembled boxes',
  'Full container wooden frames and components',
  'Custom orders available'
];

/**
 * The flyer's container-load panel and how to order, as one band, the way the
 * flyer's own intro panel groups them. They used to be two sections that
 * repeated each other (two Call buttons for the same number, shipping stated
 * twice, the address again above the footer) and pushed the first price 1.6
 * screens down on a phone. Wording follows the flyer; the pickup terms come
 * from pickup.ts.
 */
const OrderingBand = () => (
  <section className="border-t-2 border-amber-500 bg-gradient-to-b from-navy-900 to-navy-950">
    <div className="mx-auto grid max-w-6xl gap-6 px-4 py-6 sm:px-6 md:grid-cols-[minmax(0,1fr)_20rem] md:gap-10 lg:grid-cols-[minmax(0,1fr)_24rem] lg:px-8">
      <div>
        <h2 className="flex items-center gap-3 text-sm font-extrabold uppercase tracking-wide text-white sm:text-base">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-400 text-navy-950" aria-hidden="true">
            {/* Same truck as the product-row shipping pill. */}
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z" />
              <circle cx="7" cy="18" r="1.6" />
              <circle cx="17.5" cy="18" r="1.6" />
            </svg>
          </span>
          Direct full container load shipments of:
        </h2>
        <ul className="mt-4 grid gap-y-2 text-sm text-gray-200">
          {CONTAINER_ITEMS.map(item => (
            <li key={item} className="flex items-start gap-2">
              <svg className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {item}
            </li>
          ))}
        </ul>
      </div>

      <div className="border-t border-white/10 pt-5 md:border-l md:border-t-0 md:pl-10 md:pt-0">
        <h2 className="text-sm font-extrabold uppercase tracking-wide text-amber-400 sm:text-base">How to order</h2>
        <p className="mt-3 text-sm text-gray-200">
          Call or text Charles for pricing &amp; current container load specials.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <a
            href={PHONE_HREF}
            aria-label={`Call Charles at ${BUSINESS_PHONE}`}
            className="inline-flex min-h-[44px] items-center rounded-full bg-amber-400 px-5 text-xs font-extrabold uppercase tracking-wide text-amber-900 ring-1 ring-inset ring-amber-600 hover:bg-amber-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-950"
          >
            Call {BUSINESS_PHONE}
          </a>
          {/* Styled as the footer's Text pill, which sits on the same navy. */}
          <a
            href={SMS_HREF}
            aria-label={`Text Charles at ${BUSINESS_PHONE}`}
            className="inline-flex min-h-[44px] items-center rounded-full px-5 text-xs font-extrabold uppercase tracking-wide text-amber-400 ring-1 ring-inset ring-amber-500 transition-colors hover:bg-amber-500 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-950"
          >
            Text
          </a>
        </div>
        <dl className="mt-4 space-y-2 text-sm text-gray-300">
          <div>
            <dt className="inline font-semibold text-white">Shipping or pickup: </dt>
            <dd className="inline">
              Fast &amp; reliable shipping across America, or collect in{' '}
              {LOCATIONS.map((location, index) => (
                <span key={location.city}>
                  {index > 0 && ' or '}
                  <a
                    href={location.mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Map to ${location.address}`}
                    className="rounded text-white underline decoration-amber-400 underline-offset-2 hover:text-amber-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
                  >
                    {location.city}
                  </a>
                </span>
              ))}
              .
            </dd>
          </div>
          <div>
            <dt className="inline font-semibold text-white">Open: </dt>
            <dd className="inline">{PICKUP_HOURS}</dd>
          </div>
          <div>
            <dt className="inline font-semibold text-white">Payment: </dt>
            <dd className="inline">{PAYMENT_METHODS}</dd>
          </div>
        </dl>
      </div>
    </div>
  </section>
);

export default function Home() {
  // const { getItemCount } = useCart();

  // One open row at a time. Switching rows is a single state change, so the
  // click is delivered before anything reflows — the previous implementation
  // closed the old row on `pointerdown`, which moved the button out from under
  // the pointer and swallowed the click.
  const [openId, setOpenId] = useState<number | null>(null);

  // Keeps the row you clicked visually still while the drawer above it closes.
  const anchor = useRef<{ el: HTMLElement; top: number } | null>(null);

  const toggleRow = (id: number, el: HTMLElement) => {
    anchor.current = { el, top: el.getBoundingClientRect().top };
    setOpenId(cur => (cur === id ? null : id));
  };

  useLayoutEffect(() => {
    const a = anchor.current;
    anchor.current = null;
    if (!a) return;
    const delta = a.el.getBoundingClientRect().top - a.top;
    if (delta) window.scrollBy(0, delta);
  }, [openId]);

  // Escape, or a click that lands outside every row, closes the open one.
  // Bound on `click` (not `pointerdown`) so a click on another row reaches that
  // row first; and skipped entirely when nothing is open.
  useEffect(() => {
    if (openId === null) return;
    const onClick = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest?.('[data-row]')) setOpenId(null);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenId(null);
    };
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openId]);

  return (
    <div className="bg-white">
      {/* Logo and Cart in the top corners. The logo sits on a white disc: its
          navy figure disappears against the navy hero (transparent was tried
          and rejected). The disc is sized to the emblem, leaving a 2-3px white
          rim. The emblem fills 84% of the PNG's height and the PNG has
          transparent side margins, so the image is drawn at 108% of the disc,
          centred absolutely, and overhangs it invisibly. */}
      <div className="absolute top-2 left-2 z-50 h-9 w-9 rounded-full bg-white shadow-lg sm:top-4 sm:left-4 sm:h-10 sm:w-10 md:h-12 md:w-12 lg:top-6 lg:left-6 lg:h-14 lg:w-14">
        <img
          alt="Tampa Bee Supply"
          src={logo}
          className="absolute left-1/2 top-1/2 h-[108%] w-auto max-w-none -translate-x-1/2 -translate-y-1/2"
        />
      </div>

      {/* <Link
        to="/cart"
        className="absolute top-2 right-2 sm:top-4 sm:right-4 z-50 lg:top-6 lg:right-6 bg-amber-500 rounded-full p-2 sm:p-3 shadow-lg hover:shadow-xl transition-all hover:bg-amber-600"
      >
        <div className="relative">
          <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          {getItemCount() > 0 && (
            <span className="absolute -top-2 -right-2 bg-white text-amber-600 text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center border-2 border-amber-600">
              {getItemCount()}
            </span>
          )}
        </div>
      </Link> */}

      {/* Hero. The text sets the height, with a viewport-relative minimum, and
          the image fills in behind it: at a fixed height, a line that wraps on
          a short phone spilled out over the page below. */}
      <div className="relative flex min-h-[40vh] flex-col items-center justify-center px-4 py-6 sm:min-h-[35vh]">
        {/* LCP element: eager, high priority, WebP first with a JPEG fallback.
            The flyer's navy and gold honeycomb, with two real product photos at
            the sides. The middle is kept plain for the text, and phones crop to
            the middle, so the products only show on wider screens. The image is
            dark by design, so it needs no overlay for the white text. */}
        <picture>
          <source srcSet="/images/hero.webp" type="image/webp" />
          <img
            alt=""
            src="/images/hero.jpg"
            width={2400}
            height={600}
            decoding="async"
            // React 18 does not map fetchPriority to the DOM attribute; it warns
            // and drops it. Spread the lowercase name so the hint actually lands.
            {...{ fetchpriority: 'high' }}
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
        </picture>

        <div className="relative flex flex-col items-center">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white drop-shadow-2xl mb-2 animate-slide-up text-center">
            Tampa Bee Supply{' '}
            <span className="block">Wholesale</span>
          </h1>
          <p className="text-amber-400 text-base sm:text-lg md:text-xl lg:text-2xl font-semibold drop-shadow-lg mb-2 animate-slide-up animate-delay-200 text-center">We support commercial beekeepers across America</p>
          <div className="h-0.5 w-24 sm:w-32 bg-gradient-to-r from-amber-400 to-amber-600 rounded-full animate-slide-up animate-delay-200 mb-4"></div>
          <div className="max-w-3xl backdrop-blur-sm bg-black/40 rounded-lg p-3 sm:p-4 md:p-5 animate-slide-up animate-delay-400">
            <p className="text-white text-xs sm:text-sm md:text-base leading-relaxed drop-shadow-lg text-center">
              We are established commercial beekeepers headquartered in Tampa, Florida, committed to providing premium-quality beekeeping equipment at highly competitive, factory-direct prices. Every product we offer is backed by a 100% quality guarantee, reflecting our dedication to excellence, customer satisfaction, and long-term value for the beekeeping community.
            </p>
          </div>
        </div>
      </div>

      <OrderingBand />

      <div className="relative mx-auto max-w-6xl px-4 pb-8 pt-8 sm:px-6 sm:pb-10 lg:px-8">
        {GROUPS.map(group => {
          const items = productsInGroup(group);
          if (items.length === 0) return null;
          return (
            <section key={group} className="mb-8 sm:mb-10">
              <h2 className="mb-3 border-b-2 border-amber-500 pb-2 text-base font-extrabold tracking-tight text-navy-900 sm:text-lg">
                {group}
              </h2>
              <ul>
                {items.map(item => (
                  <ProductRow
                    key={item.id}
                    item={item}
                    open={openId === item.id}
                    onToggle={el => toggleRow(item.id, el)}
                  />
                ))}
              </ul>
            </section>
          );
        })}
      </div>

      <footer className="relative bg-gradient-to-b from-navy-900 to-navy-950 py-6 border-t-2 border-amber-500">
        {/* Same width as the page content above, so the left edges line up. */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <h2 className="text-amber-400 text-base font-bold mb-2">Contact Us</h2>
              <div className="space-y-1">
                {/* The flyer says "Call/Text", so both are offered: the number
                    dials, the pill opens a message. E.164 so the handoff is
                    unambiguous to the dialler. */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <a href="tel:+12517515962" className="flex min-h-[44px] items-center space-x-2 text-gray-300 hover:text-amber-400 transition-colors group rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-900">
                    <PhoneIcon className="h-5 w-5 shrink-0 text-amber-500 group-hover:text-amber-400" />
                    <span className="text-sm">Charles &mdash; (251) 751-5962</span>
                  </a>
                  <a href="sms:+12517515962" aria-label="Text Charles at (251) 751-5962" className="inline-flex min-h-[44px] shrink-0 items-center rounded-full px-3.5 text-xs font-bold uppercase tracking-wide text-amber-400 ring-1 ring-inset ring-amber-500 hover:bg-amber-500 hover:text-gray-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-900">
                    Text
                  </a>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <a href="tel:+19546823306" className="flex min-h-[44px] items-center space-x-2 text-gray-300 hover:text-amber-400 transition-colors group rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-900">
                    <PhoneIcon className="h-5 w-5 shrink-0 text-amber-500 group-hover:text-amber-400" />
                    <span className="text-sm">Jason &mdash; (954) 682-3306</span>
                  </a>
                  <a href="sms:+19546823306" aria-label="Text Jason at (954) 682-3306" className="inline-flex min-h-[44px] shrink-0 items-center rounded-full px-3.5 text-xs font-bold uppercase tracking-wide text-amber-400 ring-1 ring-inset ring-amber-500 hover:bg-amber-500 hover:text-gray-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-900">
                    Text
                  </a>
                </div>
                <a href="mailto:charles.beesupply@gmail.com" className="flex min-h-[44px] items-center space-x-2 text-gray-300 hover:text-amber-400 transition-colors group rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-900">
                  <MailIcon className="h-5 w-5 shrink-0 text-amber-500 group-hover:text-amber-400" />
                  <span className="text-sm">charles.beesupply@gmail.com</span>
                </a>
              </div>
            </div>

            <div>
              <h2 className="text-amber-400 text-base font-bold mb-2">Locations</h2>
              <div className="space-y-1">
                {LOCATIONS.map(location => (
                  <a key={location.city} href={location.mapsUrl} target="_blank" rel="noopener noreferrer" className="flex min-h-[44px] items-center space-x-2 text-gray-300 hover:text-amber-400 transition-colors group rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-900">
                    <MapPinIcon className="h-5 w-5 shrink-0 text-amber-500 group-hover:text-amber-400" />
                    <span className="text-sm">{location.address}</span>
                  </a>
                ))}
              </div>
              <dl className="mt-3 space-y-1 text-sm text-gray-300">
                <div>
                  <dt className="inline font-semibold text-white">Open: </dt>
                  <dd className="inline">{PICKUP_HOURS}</dd>
                </div>
                <div>
                  <dt className="inline font-semibold text-white">Payment: </dt>
                  <dd className="inline">{PAYMENT_METHODS}</dd>
                </div>
              </dl>
            </div>
          </div>

          <div className="border-t border-white/10 pt-3">
            <p className="text-xs text-gray-400 text-center">© {new Date().getFullYear()} Tampa Bee Supply Wholesale LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
